/**
 * What the agent did in a reply that the person should see without opening
 * the activity card: a fact it remembered, a message it passed to a piece of
 * its work, work it started or moved, and changes it is publishing (a live
 * review card). Every
 * chat is the same agent, so these are how a person sees that something said
 * here will carry into their other chats and the agent's work.
 */

const parseResult = (value) => {
  if (typeof value !== 'string') return value && typeof value === 'object' ? value : null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const STEP_WORDS = { met: 'done', partially_met: 'in progress', not_met: 'not done', unknown: 'not started' };
// Tools whose change waits for the person's approval on a card (an OS Revision, a connector's read list).
const PUBLICATION_TOOLS = new Set(['publish_os_branch', 'publish_secure_connector_definition']);

/** One outcome per successful visible tool call, in order: { key, kind, text, card? }. */
export function agentOutcomes(blocks = []) {
  const argsByCall = new Map();
  const results = [];
  (Array.isArray(blocks) ? blocks : []).forEach((block) => {
    if (block?.type === 'tool_call') {
      if (block.toolCallId) argsByCall.set(String(block.toolCallId), block.args || {});
      if (block.result != null) results.push({ key: block.toolCallId || block.id, toolName: block.toolName, args: block.args || {}, result: block.result });
    } else if (block?.type === 'tool_result' && !block.isError) {
      results.push({
        key: block.toolCallId || block.id,
        toolName: block.toolName,
        args: (block.toolCallId && argsByCall.get(String(block.toolCallId))) || {},
        result: block.result,
      });
    }
  });
  const seen = new Set();
  const outcomes = [];
  results.forEach(({ key, toolName, args, result: raw }) => {
    const dedupe = key ? String(key) : null;
    if (dedupe && seen.has(dedupe)) return;
    const result = parseResult(raw);
    // A publication waiting for the person shows its card: only Approve there publishes it.
    const publicationCard = PUBLICATION_TOOLS.has(toolName) && result?.card
      && (result.card.approval_request_id || result.card.card_type === 'revision_review') ? result.card : null;
    if (!result || (result.success === false && !publicationCard) || result.busy) return;
    const id = dedupe || `${toolName}-${outcomes.length}`;
    let outcome = null;
    switch (toolName) {
      case 'publish_os_branch':
      case 'publish_secure_connector_definition': {
        if (!publicationCard) break;
        // Repeated calls for one revision show only its latest state.
        const revision = publicationCard.revision_id || publicationCard.approval_request_id;
        const earlier = outcomes.findIndex((item) => item.kind === 'card' && item.revision === revision);
        if (earlier >= 0) outcomes.splice(earlier, 1);
        outcome = { kind: 'card', card: publicationCard, revision };
        break;
      }
      case 'remember': {
        const fact = String(result.fact || args.fact || '').trim();
        outcome = {
          kind: 'memory',
          text: result.scope === 'private' || args.scope === 'private' ? `Noted for you: ${fact}` : `Remembered: ${fact}`,
        };
        break;
      }
      case 'rewrite_agent_memory':
        outcome = { kind: 'memory', text: 'Tidied up memory' };
        break;
      case 'write_document': {
        // Memory and what the agent has learned carry into every chat; work journals don't need a note.
        const path = String(result.path || args.path || '');
        if (path === 'memory.md' || path === 'notes.md') outcome = { kind: 'memory', text: 'Tidied up memory' };
        else if (path.startsWith('knowledge/')) {
          const topic = path.slice('knowledge/'.length).replace(/\.md$/, '').replace(/[-_]+/g, ' ');
          outcome = { kind: 'memory', text: `Wrote down what I know about ${topic}` };
        }
        break;
      }
      case 'message_initiative':
        outcome = {
          kind: 'handoff',
          text: `Passed on to ${result.title || 'the work'}`,
          initiativeId: result.initiative_id || args.initiative_id || null,
        };
        break;
      case 'start_work':
      case 'start_proposed_work':
        // Work it already had is not news.
        if (!result.already) {
          outcome = { kind: 'started', text: `Started ${result.title || args.title || 'the work'}`, initiativeId: result.initiative_id || null };
        }
        break;
      case 'update_work_step': {
        const step = result.step && typeof result.step === 'object' ? result.step : {};
        const label = step.description || args.step_key || 'a step';
        const status = STEP_WORDS[step.status || args.status] || 'updated';
        outcome = { kind: 'step', text: `${result.work ? `${result.work}: ` : ''}${label} → ${status}`, initiativeId: args.initiative_id || null };
        break;
      }
      // Replies saved before agents started their own work show the suggestion's live card.
      case 'propose_work':
        if (result.card && result.card.card_type) outcome = { kind: 'card', card: result.card };
        break;
      case 'create_initiative_draft':
      case 'update_initiative_draft': {
        // Replies saved before drafts carried their card get one from the draft's id.
        const card = result.card?.card_type
          ? result.card
          : result.initiative_id && { card_type: 'work_proposal', initiative_id: result.initiative_id, initiative_title: result.title || args.title || '' };
        if (card) outcome = { kind: 'card', card };
        break;
      }
      default:
        break;
    }
    if (!outcome) return;
    if (dedupe) seen.add(dedupe);
    // One live card per piece of work, however many times the reply touched it.
    // Publication cards have no work id; they are replaced per revision above.
    const workId = outcome.kind === 'card' && outcome.card.initiative_id ? `card:${outcome.card.initiative_id}` : null;
    if (workId && seen.has(workId)) return;
    if (workId) seen.add(workId);
    outcomes.push({ key: id, ...outcome });
  });
  return outcomes;
}
